/**
 * One employee's salary, as a list of versions.
 *
 * A raise is a new version, not an edit — a payslip records the structure it
 * was computed from, so editing that row would rewrite what someone was paid.
 * The API enforces this; the panel reflects it by offering "new version" as
 * the ordinary action and no edit affordance at all on a version that has
 * already been paid against.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { money } from '../lib/money';
import type { Employee, Page, SalaryStructure } from '../types';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function SalaryPanel({ employee }: { employee: Employee }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const structures = useQuery<Page<SalaryStructure>>({
    queryKey: ['salary-structures', employee.id],
    queryFn: async () =>
      (await client.get('/api/v1/admin/payroll/salary-structures', {
        params: { employeeId: employee.id, includeInactive: true, limit: 100 },
      })).data,
  });

  const createStructure = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/payroll/salary-structures', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salary-structures', employee.id] });
      setShowForm(false);
      toast.success('New salary version created');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = structures.data?.items ?? [];
  const current = rows.find((s) => s.effectiveTo === null && s.isActive);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {employee.fullName ?? employee.employeeCode}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {employee.employeeCode}
            {employee.designation && ` · ${employee.designation}`}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowForm((v) => !v)}
        >
          <Plus className="h-4 w-4" />
          {showForm ? 'Cancel' : current ? 'New version' : 'Set salary'}
        </button>
      </div>

      {showForm && (
        <SalaryForm
          employeeId={employee.id}
          previous={current}
          submitting={createStructure.isPending}
          onSubmit={createStructure.mutate}
        />
      )}

      {structures.isLoading ? (
        <div className="flex justify-center py-6 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-200">
          No salary set. This employee will be skipped by a payroll run — loudly,
          with a reason, rather than silently paid nothing.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">In force</th>
                <th scope="col" className="px-3 py-2 font-medium">Basic</th>
                <th scope="col" className="px-3 py-2 font-medium">Gross</th>
                <th scope="col" className="px-3 py-2 font-medium">Applies</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((s) => (
                <tr key={s.id} className={s.isActive ? '' : 'opacity-60'}>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                    {s.effectiveFrom} →{' '}
                    {s.effectiveTo ?? (
                      <span className="badge bg-emerald-100 text-emerald-700">current</span>
                    )}
                    {!s.isActive && (
                      <span className="badge ml-1 bg-gray-200 text-gray-600">disabled</span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-gray-600">{money(s.basic)}</td>
                  <td className="px-3 py-2 tabular-nums text-gray-900">
                    {money(s.grossMonthly)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {[
                      s.pfApplicable && 'PF',
                      s.esiApplicable && 'ESI',
                      s.ptApplicable && 'PT',
                      s.tdsApplicable && 'TDS',
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'none'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SalaryForm({
  employeeId,
  previous,
  submitting,
  onSubmit,
}: {
  employeeId: string;
  previous?: SalaryStructure;
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  // Carried over from the version being replaced: a raise usually changes one
  // component, and retyping the rest invites a transcription error.
  const [form, setForm] = useState({
    effectiveFrom: today(),
    basic: previous?.basic ?? '',
    hra: previous?.hra ?? '0.00',
    conveyance: previous?.conveyance ?? '0.00',
    medicalAllowance: previous?.medicalAllowance ?? '0.00',
    specialAllowance: previous?.specialAllowance ?? '0.00',
    otherAllowances: previous?.otherAllowances ?? '0.00',
    otherDeductions: previous?.otherDeductions ?? '0.00',
    pfApplicable: previous?.pfApplicable ?? true,
    esiApplicable: previous?.esiApplicable ?? true,
    ptApplicable: previous?.ptApplicable ?? true,
    tdsApplicable: previous?.tdsApplicable ?? true,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const earnings = [
    form.basic,
    form.hra,
    form.conveyance,
    form.medicalAllowance,
    form.specialAllowance,
    form.otherAllowances,
  ];
  // Preview only. The authoritative figure is computed server-side, which is
  // why the table above reads grossMonthly rather than re-adding these.
  const previewGross = earnings.reduce((sum, v) => sum + (Number(v) || 0), 0);

  return (
    <form
      className="card space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ employeeId, ...form });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="s-from">Effective From</label>
          <input
            id="s-from"
            type="date"
            className="input"
            value={form.effectiveFrom}
            onChange={(e) => set('effectiveFrom', e.target.value)}
            required
          />
          {previous && (
            <p className="mt-1 text-xs text-gray-500">
              Must be after {previous.effectiveFrom}; that version is closed the
              day before this date.
            </p>
          )}
        </div>
        <MoneyField
          id="s-basic"
          label="Basic"
          value={form.basic}
          onChange={(v) => set('basic', v)}
          required
          hint="Statutory deductions are computed from this."
        />
        <MoneyField id="s-hra" label="HRA" value={form.hra} onChange={(v) => set('hra', v)} />
        <MoneyField
          id="s-conv"
          label="Conveyance"
          value={form.conveyance}
          onChange={(v) => set('conveyance', v)}
        />
        <MoneyField
          id="s-med"
          label="Medical allowance"
          value={form.medicalAllowance}
          onChange={(v) => set('medicalAllowance', v)}
        />
        <MoneyField
          id="s-spec"
          label="Special allowance"
          value={form.specialAllowance}
          onChange={(v) => set('specialAllowance', v)}
        />
        <MoneyField
          id="s-other"
          label="Other allowances"
          value={form.otherAllowances}
          onChange={(v) => set('otherAllowances', v)}
        />
        <MoneyField
          id="s-deduct"
          label="Recurring deductions"
          value={form.otherDeductions}
          onChange={(v) => set('otherDeductions', v)}
          hint="Loans and advances — not statutory deductions."
        />
      </div>

      <p className="flex items-center gap-1.5 text-sm text-gray-600">
        <Wallet className="h-4 w-4 text-gray-400" />
        Monthly gross <strong className="tabular-nums">{money(previewGross.toFixed(2))}</strong>
      </p>

      <fieldset className="rounded-xl border border-gray-200 p-3">
        <legend className="px-1 text-sm font-medium text-gray-900">Statutory Schemes</legend>
        <div className="mt-1 flex flex-wrap gap-4">
          {([
            ['pfApplicable', 'PF'],
            ['esiApplicable', 'ESI'],
            ['ptApplicable', 'PT'],
            ['tdsApplicable', 'TDS'],
          ] as const).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={form[key]}
                onChange={(e) => set(key, e.target.checked)}
              />
              {label}
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          These are opt-outs. A scheme applies only when the company policy
          enables it <em>and</em> the employee is ticked here, so unticking one
          exempts this person from a scheme that is otherwise on.
        </p>
      </fieldset>

      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {previous ? 'Create new version' : 'Set salary'}
      </button>
    </form>
  );
}

function MoneyField({
  id,
  label,
  value,
  onChange,
  required,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>{label}</label>
      <input
        spellCheck={false}
        id={id}
        className="input font-mono"
        inputMode="decimal"
        placeholder="0.00"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
